import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, ElementRef, computed, input, model, viewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icons/icon/icon.component';
import { Checklist, ChecklistGroup, ChecklistItem } from './checklist-builder.interface';

@Component({
  selector: 'ui-checklist-builder',
  templateUrl: './checklist-builder.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, DragDropModule, MatFormFieldModule, MatInputModule, ButtonComponent, IconComponent],
})
export class ChecklistBuilderComponent {
  readonly checklist = model<Checklist>({ groups: [] });
  readonly disabled = input<boolean>(false);

  readonly groupTitleInputs = viewChildren<ElementRef<HTMLInputElement>>('groupTitleInput');
  readonly itemInputs = viewChildren<ElementRef<HTMLInputElement>>('itemInput');

  // Computed to ensure we always have a valid checklist
  readonly currentChecklist = computed(() => this.checklist() || { groups: [] });

  updateGroupTitle(groupIndex: number, title: string): void {
    if (this.disabled()) return;

    const updated = JSON.parse(JSON.stringify(this.currentChecklist()));
    updated.groups[groupIndex].title = title;
    this.checklist.set(updated);
  }

  updateItemContent(groupIndex: number, itemIndex: number, content: string): void {
    if (this.disabled()) return;

    const updated = JSON.parse(JSON.stringify(this.currentChecklist()));
    updated.groups[groupIndex].items[itemIndex].content = content;
    this.checklist.set(updated);
  }

  addGroup(): void {
    if (this.disabled()) return;

    const updated = JSON.parse(JSON.stringify(this.currentChecklist()));
    const newGroup: ChecklistGroup = {
      title: '',
      items: [],
    };
    updated.groups.push(newGroup);
    this.checklist.set(updated);

    // Focus on the newly created group's title input after view updates
    setTimeout(() => {
      const inputs = this.groupTitleInputs();
      const lastInput = inputs[inputs.length - 1];
      if (lastInput) {
        lastInput.nativeElement.focus();
      }
    });
  }

  removeGroup(groupIndex: number): void {
    if (this.disabled()) return;

    const updated = JSON.parse(JSON.stringify(this.currentChecklist()));
    updated.groups.splice(groupIndex, 1);
    this.checklist.set(updated);
  }

  addItem(groupIndex: number): void {
    if (this.disabled()) return;

    const updated = JSON.parse(JSON.stringify(this.currentChecklist()));
    const newItem: ChecklistItem = {
      content: '',
    };
    updated.groups[groupIndex].items.push(newItem);
    this.checklist.set(updated);

    // Focus on the newly created item input after view updates
    setTimeout(() => {
      const inputs = this.itemInputs();
      // Calculate the index of the newly added item across all groups
      let totalItemIndex = 0;
      for (let i = 0; i < groupIndex; i++) {
        totalItemIndex += updated.groups[i].items.length;
      }
      totalItemIndex += updated.groups[groupIndex].items.length - 1; // -1 because we want the last item in the current group

      const targetInput = inputs[totalItemIndex];
      if (targetInput) {
        targetInput.nativeElement.focus();
      }
    });
  }

  removeItem(groupIndex: number, itemIndex: number): void {
    if (this.disabled()) return;

    const updated = JSON.parse(JSON.stringify(this.currentChecklist()));
    updated.groups[groupIndex].items.splice(itemIndex, 1);
    this.checklist.set(updated);
  }

  dropGroup(event: CdkDragDrop<ChecklistGroup[]>): void {
    if (this.disabled()) return;

    const updated = JSON.parse(JSON.stringify(this.currentChecklist()));
    moveItemInArray(updated.groups, event.previousIndex, event.currentIndex);
    this.checklist.set(updated);
  }

  dropItem(event: CdkDragDrop<ChecklistItem[]>, groupIndex: number): void {
    if (this.disabled()) return;

    const updated = JSON.parse(JSON.stringify(this.currentChecklist()));
    moveItemInArray(updated.groups[groupIndex].items, event.previousIndex, event.currentIndex);
    this.checklist.set(updated);
  }
}
